import { STORE_API_URL } from '@/shared/env';
import {
  ChunkedNav,
  GenericNavItem,
  NavigationItemProps,
  NavigationProps,
} from '@/types/Navigation';

import { Locale } from './LangHelper';

export enum NavItemLocations {
  App = 'App',
  AppChips = 'App Chips',

  AllCategories = 'All Categories',
  Retailers = 'Retailers',

  WebTop = 'Web top',

  Footer = 'Footer',
  FooterRegional = 'Regional Footer',
}

export type NavigationLocationDTO = {
  id: string;
  type: string;
  attributes: {
    name: NavItemLocations;
  };
  relationships: {
    [name: string]: {
      data: { type: string; id: string }[];
    };
  };
};

export type NavigationItemDTO = {
  id: string;
  type: string;
  attributes: {
    row: number;
    column: number;
    link: string;
    bold: boolean;
    title: string;
    color: {
      name: string;
      hex_code: string;
    };
    category_id: any;
    navigation_id: number;
    parent_id: number;
    updated_at: string;
    slug: null | string;
  };
  relationships: {
    [name: string]: any;
  };
  links: {
    self: string;
  };
};

export type NavItem = {
  id: string;
  isLead: boolean;

  name: string;
  link: string;

  children: NavItem[];
};

export interface FooterMatrix {
  [row: number]: {
    [column: number]: NavItem;
  };
}

const cachedFetchedFooterItems: { [locale in Locale]?: FooterMatrix[] } = {};
const cachedFetchedHeaderItems: { [locale in Locale]?: ChunkedNav } = {};

export const NAVIGATION_ENDPOINT = `${STORE_API_URL}/v1/navigations?locale=`;
export const ALL_NAVIGATION_ITEMS_ENDPOINT = `${STORE_API_URL}/v1/navigation_items?locale=`;

const storeFetchAPIPromise: { [locale in Locale]?: Promise<FooterMatrix[]> } = {};
const storeFetchHeaderAPIPromise: { [locale in Locale]?: Promise<ChunkedNav> } = {};

const fetchNavigations = async (
  locale: Locale
): Promise<{ data: NavigationProps[]; included: NavigationItemProps[] }> => {
  try {
    const response = await fetch(NAVIGATION_ENDPOINT + locale);
    return await response.json();
  } catch (err) {
    console.error(err);
    return { data: [], included: [] };
  }
};

const fetchAllNavigationItems = async (locale: Locale): Promise<Array<NavigationItemProps>> => {
  try {
    const response = await fetch(ALL_NAVIGATION_ITEMS_ENDPOINT + locale);
    return (await response.json()).data;
  } catch (err) {
    console.error(err);
    return [];
  }
};

export async function fetchGlobalNavItems(locale: Locale): Promise<FooterMatrix[]> {
  if (storeFetchAPIPromise[locale]) {
    return (await storeFetchAPIPromise[locale]) as FooterMatrix[];
  }

  if (cachedFetchedFooterItems[locale]) {
    return cachedFetchedFooterItems[locale] as FooterMatrix[];
  }

  storeFetchAPIPromise[locale] = (async () => {
    const response = await fetchNavigations(locale);

    const { items, regionalItems } = getNavMatrix(
      response,
      NavItemLocations.Footer,
      NavItemLocations.FooterRegional
    );

    return [items, regionalItems];
  })();

  const response = await storeFetchAPIPromise[locale];

  delete storeFetchAPIPromise[locale];
  cachedFetchedFooterItems[locale] = response;

  return response as FooterMatrix[];
}

export async function fetchHeaderNavItems(locale: Locale): Promise<ChunkedNav> {
  if (storeFetchHeaderAPIPromise[locale]) {
    return (await storeFetchHeaderAPIPromise[locale]) || [];
  }

  if (cachedFetchedHeaderItems[locale]) {
    return cachedFetchedHeaderItems[locale] || [];
  }

  storeFetchHeaderAPIPromise[locale] = (async () => {
    const allCategoriesNavItem = await getNavigationItemByName(
      NavItemLocations.AllCategories,
      locale
    );
    const allCategoriesItems = await fetchAllNavigationItems(locale);
    const allCategoriesSubMenuItems = allCategoriesItems.filter(
      (item) => String(item.attributes.navigation_id) === allCategoriesNavItem?.id
    );

    const allCategoriesChunks = chunkNavigationItems(allCategoriesSubMenuItems);

    const webTopNavItems = await fetchNavigation(NavItemLocations.WebTop, locale);

    return [[allCategoriesNavItem, allCategoriesChunks], ...webTopNavItems] as ChunkedNav;
  })();

  const response = await storeFetchHeaderAPIPromise[locale];

  delete storeFetchHeaderAPIPromise[locale];
  cachedFetchedHeaderItems[locale] = response;

  return response || [];
}

const getNavMatrix = (data: any, navName: NavItemLocations, navNameLocation?: NavItemLocations) => {
  const regionalItems: FooterMatrix = {};
  const items: FooterMatrix = {};

  let regionNavID: number;
  let navID: number;

  // we only care about Footer & Regional footer, so let's start by filtering the pool
  data.data.forEach((item: NavigationLocationDTO) => {
    if (item.attributes.name === navName) {
      navID = parseInt(item.id);
    } else if (navNameLocation && item.attributes.name === navNameLocation) {
      regionNavID = parseInt(item.id);
    }
  });

  const mappedNavItems: { [id: string]: NavigationItemDTO } = {};
  data.included.forEach((item: NavigationItemDTO) => {
    if ([regionNavID, navID].includes(item.attributes.navigation_id)) {
      mappedNavItems[item.id] = item;
    }
  });

  const placeNavItemInMatrix = (item: NavigationItemDTO, isRegional = false) => {
    const matrix = isRegional ? regionalItems : items;

    if (matrix[item.attributes.column] === undefined) {
      matrix[item.attributes.column] = {};
    }

    let targetColumn;
    let targetRow;
    let isLead = false;

    if (item.attributes.parent_id) {
      const parentItem = mappedNavItems[item.attributes.parent_id];
      if (!matrix[parentItem.attributes.column][parentItem.attributes.row]) {
        placeNavItemInMatrix(parentItem, isRegional);
      }

      targetColumn = parentItem.attributes.column;
      targetRow = item.attributes.row + 1;
    } else {
      targetColumn = item.attributes.column;
      targetRow = item.attributes.row;

      if (!isRegional) {
        isLead = true;
      }
    }

    matrix[targetColumn][targetRow] = {
      name: item.attributes.title,
      id: item.id,
      link: item.attributes.link,
      children: [],
      isLead,
    };
  };

  Object.values(mappedNavItems).forEach((item: NavigationItemDTO) => {
    placeNavItemInMatrix(item, item.attributes.navigation_id == regionNavID);
  });

  return { regionalItems, items };
};

const byColumn = (a: NavigationItemProps, b: NavigationItemProps) =>
  a?.attributes?.column > b?.attributes?.column ? 1 : -1;

const fetchNavigation = async (name: NavItemLocations, locale: Locale): Promise<ChunkedNav> => {
  const navigations = await fetchNavigations(locale);
  const navigation = navigations.data.find((nav) => nav.attributes.name === name);

  if (!navigation) {
    throw new Error(`Unable to find a navigation with name "${name}"`);
  }

  const allNavigationItems = await fetchAllNavigationItems(locale);
  const allNavigationItemsById: any = allNavigationItems.reduce(
    (accum, item) => ({
      ...accum,
      [item.id]: item,
    }),
    {}
  );

  const { data } = navigation.relationships.navigation_items;
  const topLevelNavigationItems = allNavigationItems
    .filter(({ attributes: { parent_id } }) => parent_id === null)
    .filter(({ id }) => data.find((n) => n.id === id))
    .sort(byColumn);

  return topLevelNavigationItems.map((topLevelNavItem) => {
    const { data: childNavItems } = topLevelNavItem.relationships.sub_navigation_items;

    const genericTopLevelNavItem: GenericNavItem = {
      id: topLevelNavItem.id,
      text: topLevelNavItem.attributes.title,
      categoryId: topLevelNavItem.attributes.category_id,
      bold: topLevelNavItem.attributes.bold,
      color: topLevelNavItem.attributes.color,
      link: navLink(topLevelNavItem.attributes.link, topLevelNavItem.attributes.category_id),
    };

    const matchingChildNavItems = childNavItems.reduce((accum, item) => {
      if (allNavigationItemsById[item.id]) {
        accum.push(allNavigationItemsById[item.id]);
      }

      return accum;
    }, [] as Array<NavigationItemProps>);

    const genericChildNavItemChunks = chunkNavigationItems(matchingChildNavItems);

    return [genericTopLevelNavItem, genericChildNavItemChunks];
  });
};

const chunkNavigationItems = (
  subMenuItems: Array<NavigationItemProps>
): Array<Array<GenericNavItem>> => {
  return subMenuItems
    .reduce((accum, element) => {
      const { column, row } = element.attributes;

      if (!Array.isArray(accum[column])) {
        accum[column] = [];
      }

      accum[column][row] = {
        id: element.id,
        text: element.attributes.title ?? '',
        categoryId: element.attributes.category_id,
        bold: element.attributes.bold,
        color: element.attributes.color,
        link: navLink(element.attributes.link, element.attributes.category_id),
      };

      return accum;
    }, [] as Array<Array<GenericNavItem>>)
    .reduce((accum, chunk) => {
      const chunkWithoutEmptyValues = chunk.filter((navItem) => !!navItem);
      return [...accum, chunkWithoutEmptyValues];
    }, [] as Array<Array<GenericNavItem>>)
    .filter((chunks) => chunks?.length > 0);
};

const navLink = (link: string, categoryId: string) => {
  if (!link) return '';

  // TODO: we need to come up with a better way to handle fully qualified URLs from the CMS
  // and how we handle sharing those URLs between environments with different origins
  const afterpaySiteRegex = new RegExp('^(https?://)?(www.)?afterpay.com');
  const clearpayEUSiteRegex = new RegExp('^(https?://)?(www.)?clearpay.com');
  const clearpayGBSiteRegex = new RegExp('^(https?://)?(www.)?clearpay.co.uk');

  const isFullyQualified = afterpaySiteRegex.test(link);
  const isFullyQualifiedClearpayEU = clearpayEUSiteRegex.test(link);
  const isFullyQualifiedClearpayGB = clearpayGBSiteRegex.test(link);

  if (isFullyQualified) {
    return link.replace(afterpaySiteRegex, '');
  } else if (isFullyQualifiedClearpayEU) {
    return link.replace(clearpayEUSiteRegex, '');
  } else if (isFullyQualifiedClearpayGB) {
    return link.replace(clearpayGBSiteRegex, '');
  }

  return link && categoryId ? `/categories${link}` : link;
};

const getNavigationItemByName = async (
  name: NavItemLocations,
  locale: Locale
): Promise<GenericNavItem | null> => {
  const allCategoriesNavigation = await fetchNavigations(locale);
  const matchingNavItem = allCategoriesNavigation.data.find((nav) => nav.attributes.name === name);
  if (!matchingNavItem) {
    return null;
  }

  return {
    id: matchingNavItem.id,
    text: getTranslatedCategories(locale) || name,
    categoryId: null,
    bold: false,
    color: {
      name: 'black',
      hexCode: '#000000',
    },
    link: '/categories',
  };
};

const getTranslatedCategories = (locale: Locale): string => {
  switch (locale) {
    case 'en-US':
    case 'en-AU':
    case 'en-NZ':
    case 'en-CA':
    case 'en-GB':
      return 'All Categories';
    case 'fr-CA':
      return 'Toutes les Catégories';
    default:
      return '';
  }
};
